/**
 * @module queryParams
 */
const { Log } = require('./browserLogger');

/**
 * @typedef {object} QueryParamData
 * @property {string} name - The query parameter name.
 * @property {string} url - The URL to which to add the query param.
 * @property {string} value - The query parameter value.
 */

/**
 * Helper function that combines a base URL and URL parameters.
 *
 * @param {object} params - The function params object.
 * @param {string} params.baseUrl - The base URL.
 * @param {boolean} [params.overrideParams] - Optional boolean flag indicating whether or not to override the existing params in the URL with the incoming params.
 * @param {string} params.queryParams - The URL query parameters in the format "?key1=value1&key2=value2" or "key1=value1&key2=value2".
 *
 * @returns {string} The combined URL with the URL parameters, for an invalid base URL or URL parameters the original base URL is returned.
 */
function combineQueryParamsAndUrl({
  baseUrl,
  overrideParams = false,
  queryParams,
}) {
  try {
    const urlObject = new URL(baseUrl);
    const sanitizedParams =
      queryParams && queryParams.startsWith('?')
        ? queryParams.substring(1)
        : queryParams;
    const paramsArray = sanitizedParams ? sanitizedParams.split('&') : [];

    paramsArray.forEach((param) => {
      const [key, value] = param.split('=');
      if (key && value) {
        if (overrideParams || !urlObject.searchParams.has(key)) {
          urlObject.searchParams.set(key, value);
        }
      }
    });

    return urlObject.toString();
  } catch (error) {
    return baseUrl;
  }
}

/**
 * Convenience function to return the value of the specified query parameter, if found.
 *
 * @param {string} paramName - The name of the query parameter whose value should be returned.
 * @param {string} [url] - Optional url to inspect, which overrides the default logic to look at window.location.
 *
 * @returns {string} The value of the specified query parameter name, or null if not found.
 */
function getQueryParam(paramName, url) {
  /* istanbul ignore next */
  if (!window || !window.location) {
    return null;
  }
  let { search } = window.location;
  if (url && typeof url === 'string') {
    const urlObject = new URL(url);
    search = urlObject.search;
  }
  if (
    (!url || typeof url !== 'string') &&
    window.location.search.length === 0
  ) {
    return null;
  }
  const paramMap = {};
  const paramArray = search.substring(1).split('&');
  paramArray.forEach((item) => {
    const paramPair = item.split('=');
    paramMap[paramPair[0]] = paramPair[1] || null;
  });

  return paramMap[paramName] || null;
}

/**
 * Helper method to easily get query params from URL.
 *
 * @param {string} [paramName] - Optional query parameter to return. If omitted, a list of parameters will be returned.
 * @param {string} [url] - Optional url to inspect, which overrides the default logic to look at window.location.
 *
 * @returns {(string|object)} If a param specified and found, the string value of the parameter will be returned. If no param specified or no value is found, the full SearchParam object will be returned.
 */
function getQueryParams(paramName = '', url = null) {
  /* istanbul ignore next */
  if (!window || !window.location) {
    return null;
  }
  let { search } = window.location;
  if (url && typeof url === 'string') {
    const urlObject = new URL(url);
    search = urlObject.search;
  }
  const queryString = search;
  const urlParamsFound = new URLSearchParams(queryString);
  const queryParam = urlParamsFound.get(paramName);
  if (queryParam) {
    return queryParam;
  }
  return urlParamsFound;
}

/**
 * Convenience function to add a query/search parameter to the provided url.
 *
 * @param {object} params - The function params object.
 * @param {string} params.name - The query parameter name. If the name is already present on the url, its value will be replaced by the value provided.
 * @param {string} params.url - The URL to which to add the query param.
 * @param {string} params.value - The query parameter value. If the name is already present on the url, its value will be replaced by the value provided.
 *
 * @returns {string} The url containing the provided query parameter. In case of invalid value or type of parameters the original url will be returned. And in case of the query param name already existing on the url, its value will be overwritten by the new value provided.
 */
function addQueryParamToUrl({ name, url, value }) {
  if (
    !name ||
    !value ||
    typeof name !== 'string' ||
    typeof value !== 'string'
  ) {
    return url;
  }

  try {
    const newUrl = new URL(url);
    newUrl.searchParams.set(name, value);
    return newUrl.toString();
  } catch (error) {
    /* istanbul ignore next */
    Log.error(error);
    /* istanbul ignore next */
    return url;
  }
}

module.exports = {
  addQueryParamToUrl,
  combineQueryParamsAndUrl,
  getQueryParam,
  getQueryParams,
};
